// lib/woEdit.mjs — R115 maint: the ONE work-order edit diff. The Maintenance
// board card and the calendar's WO inspector render the same four fieldsets
// (client/src/shared/components/WoEditor.ts) and both turn what the form says
// into a POST /api/assetops/work-orders/:id body here, so the two surfaces can
// never send different bodies for the same edit (the rules moved verbatim from
// MaintenancePage's Save handler, R70 / R101 #8 / R102 #5 / R110 #2).
//
// form = raw values read from the DOM; a key that is undefined means "that
// control is not on screen" and is never diffed. Differences from the old
// inline handler, on purpose:
//   · assigned_to and summary are diffed like every other field (the board used
//     to send both on every Save, writing an "assigned → X; summary updated"
//     log line — and an assignee DM check — when nothing had changed);
//   · a cost that isn't a number is ignored instead of being sent as NaN (JSON
//     null → it silently CLEARED the stored cost);
//   · money:false drops cost / payer / billed / skill from the body (the
//     calendar hides them for manager / remote_ops; the server 403s them too).
// Returns the body, or null when nothing changed. Pure ESM, no DOM.
import { schedOf, priorityOf } from './woFormat.mjs';

export const WO_EDIT_MONEY_KEYS = Object.freeze(['cost', 'payer', 'billing_status', 'skill']);
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** {granted:true, mode} | null — the guest permission-to-access shape the edit route stores. */
export function normalizeGuestPermission(gp) {
  if (!gp || !gp.granted) return null;
  return { granted: true, mode: gp.mode === 'own_code' ? 'own_code' : 'guest_present', ...(gp.note ? { note: String(gp.note).slice(0, 300) } : {}) };
}

/** What the form shows for a record (every field) — the "nothing changed" baseline. */
export function woFormFromRecord(w) {
  const x = w || {};
  const gp = x.guest_permission;
  return {
    assigned_to: String(x.assigned_to || ''),
    role: x.role || 'maintenance',
    vendor_id: x.vendor_id ? String(x.vendor_id) : '',
    priority: priorityOf(x),
    status: x.status || 'open',
    scheduled_date: schedOf(x) || '',
    due_date: x.due_date ? String(x.due_date).slice(0, 10) : '',
    due_checkout: x.due_source === 'checkout',
    asset_id: x.asset_id ? String(x.asset_id) : '',
    cost: x.cost != null ? String(x.cost) : '',
    payer: x.payer || '',
    billing_status: x.billing_status || '',
    skill: x.skill || '',
    claim_tracked: !!x.claim_tracked,
    gp_granted: !!(gp && gp.granted),
    gp_mode: gp && gp.mode === 'own_code' ? 'own_code' : 'guest_present',
    summary: String(x.summary || ''),
  };
}

const has = (o, k) => o[k] !== undefined;
const nul = (v) => (v == null || v === '' ? null : String(v));

/**
 * diffWoEdit(current, form, {money}) → POST /work-orders/:id body (only changed keys) or null.
 * Values are validated the way the route validates them so a bad value never reaches the server.
 */
export function diffWoEdit(cur, form, opts = {}) {
  const w = cur || {};
  const f = form || {};
  const money = opts.money !== false;
  const body = {};
  if (has(f, 'assigned_to')) { const v = String(f.assigned_to).trim().slice(0, 120); if (v !== String(w.assigned_to || '').trim()) body.assigned_to = v; }
  if (has(f, 'role')) { const v = ['maintenance', 'cleaner', 'inspector'].includes(f.role) ? f.role : 'maintenance'; if (v !== (w.role || 'maintenance')) body.role = v; }
  if (has(f, 'vendor_id')) { const v = nul(f.vendor_id); if (v !== nul(w.vendor_id)) body.vendor_id = v; }
  if (has(f, 'priority')) { const v = String(f.priority || ''); if (v && ['emergency', 'urgent', 'routine', 'pm'].includes(v) && v !== priorityOf(w)) body.priority = v; }
  if (has(f, 'status')) { const v = String(f.status || ''); if (v && ['open', 'in_progress', 'done'].includes(v) && v !== (w.status || 'open')) body.status = v; }
  if (has(f, 'scheduled_date')) { const v = nul(f.scheduled_date); if ((v === null || DAY.test(v)) && v !== (schedOf(w) || null)) body.scheduled_date = v; }
  if (has(f, 'due_date')) { const v = nul(f.due_date); if ((v === null || DAY.test(v)) && v !== nul(w.due_date && String(w.due_date).slice(0, 10))) body.due_date = v; }
  if (has(f, 'due_checkout')) { const v = f.due_checkout ? 'checkout' : null; if (v !== (w.due_source === 'checkout' ? 'checkout' : null)) body.due_source = v; }
  if (has(f, 'asset_id')) { const v = nul(f.asset_id); if (v !== nul(w.asset_id)) body.asset_id = v; }
  if (money) {
    if (has(f, 'cost')) {
      const raw = String(f.cost).trim();
      const next = raw === '' ? null : Number(raw);
      if ((next === null || Number.isFinite(next)) && next !== (w.cost == null || w.cost === '' ? null : Number(w.cost))) body.cost = next;
    }
    if (has(f, 'payer')) { const v = nul(f.payer); if ((v === null || v === 'wch' || v === 'owner') && v !== nul(w.payer)) body.payer = v; }
    if (has(f, 'billing_status')) { const v = nul(f.billing_status); if ((v === null || v === 'billed_owner' || v === 'billed_os') && v !== nul(w.billing_status)) body.billing_status = v; }
    if (has(f, 'skill')) { const v = nul(f.skill); if ((v === null || v === 'low' || v === 'high') && v !== nul(w.skill)) body.skill = v; }
  }
  if (has(f, 'claim_tracked')) { if (!!f.claim_tracked !== !!w.claim_tracked) body.claim_tracked = !!f.claim_tracked; }
  if (has(f, 'gp_granted')) {
    const cur0 = w.guest_permission && w.guest_permission.granted ? w.guest_permission : null;
    const mode = f.gp_mode !== undefined ? (f.gp_mode === 'own_code' ? 'own_code' : 'guest_present') : (cur0 && cur0.mode === 'own_code' ? 'own_code' : 'guest_present');
    const next = f.gp_granted ? { granted: true, mode } : null;
    if (!!next !== !!cur0 || (next && cur0 && next.mode !== cur0.mode)) body.guest_permission = next;
  }
  if (has(f, 'summary')) { const v = String(f.summary); if (v !== String(w.summary || '')) body.summary = v; }
  return Object.keys(body).length ? body : null;
}

/** Apply a saved body to a local copy (optimistic display while the re-read lands). */
export function applyWoEdit(w, body) {
  const out = { ...(w || {}) };
  if (!body) return out;
  for (const [k, v] of Object.entries(body)) {
    if (k === 'scheduled_date') { out.scheduled_date = v; out.scheduled_window = v ? { start: v, end: v } : null; }
    else if (k === 'due_source') out.due_source = v;
    else out[k] = v;
  }
  return out;
}
